import { useEffect, useRef, useState } from "react";

import { dtmfKindLabel, isCodeCollectState } from "../lib/digits";
import { useStore } from "../lib/store";
import { cx } from "./ui";

function readLiveDigits(snapshot: unknown): string {
  if (!snapshot || typeof snapshot !== "object") return "";
  const rec = snapshot as { digits?: unknown; code?: unknown };
  const fromDigits = rec.digits;
  if (typeof fromDigits === "string" && fromDigits.trim()) return fromDigits;
  if (typeof fromDigits === "number" && Number.isFinite(fromDigits)) return String(fromDigits);
  const code = rec.code;
  if (code && typeof code === "object") {
    const entered = (code as { entered?: unknown }).entered;
    if (typeof entered === "string") return entered;
    if (typeof entered === "number" && Number.isFinite(entered)) return String(entered);
  }
  return typeof fromDigits === "string" ? fromDigits : "";
}

/**
 * Live DTMF / collected-code display. Tabular nums, pulse on each new digit.
 * Reads `snapshot.digits` or `snapshot.code.entered` — missing fields stay blank.
 */
export function DigitsLive() {
  const snapshot = useStore().state.snapshot;
  const kind = dtmfKindLabel(snapshot.state, snapshot.detail, snapshot.digitKind);
  const collecting =
    snapshot.digitKind === "otp" &&
    (isCodeCollectState(snapshot.state, snapshot.detail) || Boolean(snapshot.digits));
  // Hold paints no digits: Resend leaves snapshot.digits stale under "hold — no digits".
  const digits =
    kind !== "hold" && collecting && snapshot.digitKind !== "gate" ? readLiveDigits(snapshot) : "";
  const shown = digits.trim() ? digits.trim() : "------";
  const [pulse, setPulse] = useState(0);
  const prev = useRef(digits);

  useEffect(() => {
    if (digits && digits !== prev.current) setPulse((n) => n + 1);
    prev.current = digits;
  }, [digits]);

  const chars = shown.split("");
  const live = Boolean(digits.trim());

  return (
    <div className="flex min-h-[96px] flex-1 flex-col items-center justify-center px-4 py-3 md:min-h-[110px] md:px-6">
      <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-mute">
        {kind === "gate" ? "gate — not a code" : kind === "hold" ? "hold — no digits" : kind === "code" ? "code" : "live digits"}
      </div>
      <div
        className="tabular-digits flex items-center justify-center font-mono text-5xl font-medium text-accent md:text-6xl"
        aria-live="polite"
        aria-label={live ? `digits ${shown}` : "no digits yet"}
      >
        {chars.map((ch, i) => {
          const last = live && i === chars.length - 1;
          return (
            <span
              key={`${i}-${ch}-${last ? pulse : 0}`}
              className={cx("inline-block", last && pulse > 0 && "digit-pulse")}
            >
              {ch}
            </span>
          );
        })}
      </div>
    </div>
  );
}
